import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, Mail, Phone, Trash2, Building2, Cloud } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import AiLeadInsights from '../components/AiLeadInsights';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger
} from '../components/ui/alert-dialog';

const AdminDemoRequestsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/demo-requests');
      setItems(data);
    } catch {
      toast({ title: 'Failed to load demo requests' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const remove = async (id) => {
    try {
      await api.delete(`/demo-requests/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch {
      toast({ title: 'Delete failed' });
    }
  };

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5 bg-[#05080f]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <div className="h-4 w-px bg-white/10"></div>
            <h1 className="text-white font-display text-lg">Demo Requests</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-white text-sm">{user?.username}</div>
              <div className="text-slate-500 text-xs">{user?.email}</div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
              <LogOut size={14} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">CloudRebuild demo requests</h2>
          <p className="text-slate-400 mt-1 text-sm">Inbound leads from the CloudRebuild product page. Click AI Insights to score and draft a reply.</p>
        </div>

        {loading ? (
          <div className="text-slate-400 py-16 text-center">Loading...</div>
        ) : items.length === 0 ? (
          <div className="card-dark rounded-3xl p-12 text-center">
            <p className="text-slate-300">No demo requests yet.</p>
            <p className="text-slate-500 text-sm mt-2">They&apos;ll appear here as visitors submit the CloudRebuild form.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {items.map((r) => (
              <div key={r.id} className="card-dark rounded-2xl p-5 md:p-6" data-testid={`demo-req-${r.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-white font-semibold text-lg">{r.full_name}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white/5 text-slate-300 border-white/10 capitalize">
                        {r.source || 'cloudrebuild'}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm">
                      <a href={`mailto:${r.work_email}`} className="text-slate-300 hover:text-amber-300 inline-flex items-center gap-1.5">
                        <Mail size={13} /> {r.work_email}
                      </a>
                      {r.phone && (
                        <a href={`tel:${r.phone}`} className="text-slate-300 hover:text-amber-300 inline-flex items-center gap-1.5">
                          <Phone size={13} /> {r.phone}
                        </a>
                      )}
                      <span className="text-slate-300 inline-flex items-center gap-1.5">
                        <Building2 size={13} /> {r.company}{r.job_title ? ` · ${r.job_title}` : ''}
                      </span>
                    </div>
                    {Array.isArray(r.clouds) && r.clouds.length > 0 && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <Cloud size={13} className="text-slate-500" />
                        {r.clouds.map((c) => (
                          <span key={c} className="text-[11px] px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-200 border-amber-500/25">
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                    {r.message && (
                      <div className="mt-3 text-slate-400 text-sm leading-relaxed border-l-2 border-white/10 pl-3">
                        {r.message}
                      </div>
                    )}
                    <div className="mt-3 text-xs text-slate-500">
                      Received {new Date(r.created_at).toLocaleString()}
                    </div>

                    <div className="mt-4">
                      <AiLeadInsights
                        kind="demo_request"
                        payload={{
                          name: r.full_name, email: r.work_email, company: r.company,
                          job_title: r.job_title, phone: r.phone, clouds: r.clouds, message: r.message
                        }}
                        testIdPrefix={`demo-${r.id}`}
                      />
                    </div>
                  </div>

                  <div className="flex md:flex-col gap-2 md:min-w-[110px]">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 h-9">
                          <Trash2 size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0b1220] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this request?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(r.id)} className="bg-red-500/80 hover:bg-red-500 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDemoRequestsPage;
